'use client';

import { useRouteTracker } from '@/features/navigation';
import { VideoPlayback } from '@/features/video-playback';
import { useAutohide } from '@/hooks/useAutohide';
import { BackButton } from '@/ui/buttons/BackButton';
import { isAutoplayAudioRestricted } from '@/utils/platformUtils';
import {
  usePlayerEvents,
  useVideoProgressPosition,
} from '@vimond/features/resume-watching';
import { usePlayback } from '@vimond/features/video-playback';
import { useLocale } from '@vimond/i18n';
import { AssetType } from '@vimond/services/content-discovery';
import { PlaybackType } from '@vimond/services/play';
import { cn } from '@vimond/theme/tailwind';
import { getEnvVar } from '@vimond/utils/env';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PlaylistCountdown } from './overlay/PlaylistCountdown';
import { PlaylistItems } from './overlay/PlaylistItems';

type Props = {
  asset: AssetType;
  assets: AssetType[];
  playback?: PlaybackType;
};

const COUNTDOWN_TIMER = 5;

// TODO: Needs investigation. Component re-rendering continuously here. Check setStates & fix
export const PlaylistController = ({
  asset,
  assets,
  playback: playbackProp,
}: Props) => {
  const router = useRouter();
  const { locale } = useLocale();
  const { isFreshPageLoad, isRoutePatternChanged } = useRouteTracker(
    `${locale}/watch/`,
  );
  // Use useState to avoid hydration mismatch - audioRestricted can only be determined on client
  const [audioRestricted, setAudioRestricted] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setAudioRestricted(isAutoplayAudioRestricted(window.navigator));
    }
  }, []);
  const shouldMute =
    (audioRestricted && isRoutePatternChanged) || isFreshPageLoad; // important to mute on fresh page load for all browsers

  // State for currently playing asset
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<
    string | undefined
  >(asset.id);
  const currentlyPlayingIdRef = useRef(currentlyPlayingId);
  currentlyPlayingIdRef.current = currentlyPlayingId;

  // Playback hook
  const { error, isLoading, playback } = usePlayback(
    asset.id,
    typeof window !== 'undefined' ? window.navigator.userAgent : '',
    playbackProp,
  );
  const actualPlayback = playbackProp ?? playback;

  // Next asset state and preload playback
  const [nextAsset, setNextAsset] = useState<AssetType | undefined>();
  usePlayback(
    nextAsset?.id ?? asset.id,
    typeof window !== 'undefined' ? window.navigator.userAgent : '',
  );

  // Countdown state -> countdown between assets in playlist
  const [countdown, setCountdown] = useState<number | null>(null);
  const [countdownInterval, setCountdownInterval] =
    useState<NodeJS.Timeout | null>(null);

  const { isHidden, setIsHidden } = useAutohide();

  const endCreditsMarker = asset?.markers?.find(
    (m) => m.type === 'End-Credits',
  );

  const [userHasDisabledAutoPlay, setUserHasDisabledAutoPlay] = useState(false);

  const { postPlayerEvent, postVideoFinishedEvent } = usePlayerEvents();

  // get playback position from viewing history, or use start time
  const { progress } = useVideoProgressPosition(asset.id, locale);
  const startPosition = progress ?? actualPlayback?.startTime ?? 0;

  // Environment flags
  const isNextPreviewDisabled =
    getEnvVar('NEXT_PUBLIC_NEXT_VIDEO_OVERLAY_HIDE') === 'true';

  const isNextVideoAutoPlayDisabled =
    getEnvVar('NEXT_PUBLIC_NEXT_VIDEO_AUTOPLAY_DISABLED') === 'true';

  useEffect(() => {
    setIsHidden(false);
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [setIsHidden, countdownInterval]);

  useEffect(() => {
    if (countdown) setIsHidden(false);
  }, [countdown, setIsHidden]);

  // Handlers
  const cancelCountdown = useCallback(() => {
    if (countdownInterval) clearInterval(countdownInterval);
    setCountdown(null);
  }, [countdownInterval]);

  const onUserCancelCountdown = () => {
    setUserHasDisabledAutoPlay(true);
    cancelCountdown();
  };

  useEffect(() => {
    if (!countdown) cancelCountdown();
  }, [cancelCountdown, countdown]);

  useEffect(() => {
    // find index of current item playing
    const currentIndex =
      assets?.findIndex((a) => a.id === currentlyPlayingIdRef.current) ?? 0;
    if (!assets || currentIndex === -1 || currentIndex >= assets.length) return;
    setNextAsset(assets[currentIndex + 1]);
    setIsHidden(true);
  }, [asset, assets, setIsHidden]);

  const onPlaybackFinished = useCallback(
    (_assetId: string, countdownTimer: number = COUNTDOWN_TIMER) => {
      if (actualPlayback) {
        postVideoFinishedEvent({
          playback: actualPlayback,
          position: actualPlayback.duration || asset.duration || -1,
        });
      }

      if (isNextVideoAutoPlayDisabled) return;

      // user has canceled the countdown feature
      if (userHasDisabledAutoPlay) return;
      // already showing countdown
      if (countdown) return;
      // find index of current item playing
      const currentIndex =
        assets?.findIndex((a) => a.id === currentlyPlayingIdRef.current) ?? 0;
      if (currentIndex === undefined) return;

      const nextAsset = assets[currentIndex + 1];
      setNextAsset(nextAsset);
      if (!nextAsset) return;
      // start next element in X seconds and show a count down timer
      let timer = countdownTimer;
      setCountdown(timer);
      // Clear any existing interval before starting a new one
      if (countdownInterval) clearInterval(countdownInterval);
      const interval = setInterval(() => {
        timer--;
        setCountdown(timer);
        if (timer <= 0) {
          clearInterval(interval);
          router.push(`/watch/${nextAsset.id}`);
          setCountdown(null);
          setCurrentlyPlayingId(nextAsset.id);
          cancelCountdown();
        }
      }, 1000);
      setCountdownInterval(interval);
    },
    [
      asset.duration,
      assets,
      actualPlayback,
      postVideoFinishedEvent,
      router,
      userHasDisabledAutoPlay,
      countdown,
      countdownInterval,
      cancelCountdown,
      isNextVideoAutoPlayDisabled,
    ],
  );

  const onPlaybackPositionChanges = useCallback(
    (assetId: string, position: number) => {
      // ensure we are still playing the same asset
      if (assetId !== asset.id || assetId !== actualPlayback?.assetId) return;

      if (
        endCreditsMarker?.startTime &&
        position >= endCreditsMarker.startTime
      ) {
        onPlaybackFinished(assetId, COUNTDOWN_TIMER * 2);
      } else if (
        asset.duration &&
        position >= asset.duration - COUNTDOWN_TIMER * 2
      ) {
        onPlaybackFinished(assetId, COUNTDOWN_TIMER * 2);
      } else if (actualPlayback) {
        postPlayerEvent({ playback: actualPlayback, position });
      }
    },
    [
      asset.duration,
      asset.id,
      endCreditsMarker,
      onPlaybackFinished,
      actualPlayback,
      postPlayerEvent,
    ],
  );

  const onElementClick = useCallback(
    (id: string) => {
      const selectedAsset = assets.find((a) => a.id === id);
      if (!selectedAsset) return;
      setCurrentlyPlayingId(selectedAsset.id);
      cancelCountdown();
    },
    [assets, cancelCountdown],
  );

  // Layout flags
  const isFullScreenWatchLayout =
    usePathname()?.includes('/watch') &&
    !(getEnvVar('NEXT_PUBLIC_ALTERNATIVE_WATCH_LAYOUT') === 'true');
  const showPlaylistItems =
    (isFullScreenWatchLayout &&
      getEnvVar('NEXT_PUBLIC_VIDEO_OVERLAY_PLAYLIST_ENABLED') !== 'false' &&
      !countdown) ||
    !isFullScreenWatchLayout;

  const shouldShowLoadingSkeleton = !playbackProp && isLoading;

  // Loading skeleton
  const loadingSkeleton = (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden bg-gray-900',
        isFullScreenWatchLayout && 'h-screen w-screen',
      )}
    >
      <div className="absolute left-3 top-5 z-10">
        <BackButton />
      </div>

      <div className="flex items-center justify-center h-full w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={
          isFullScreenWatchLayout
            ? 'relative h-full w-full overflow-hidden'
            : 'relative w-screen lg:h-full lg:w-[58vw] xl:w-[65vw] lg:p-4 lg:pr-2 flex flex-col items-center gap-2 lg:gap-4 md:flex-row'
        }
      >
        {shouldShowLoadingSkeleton ? (
          loadingSkeleton
        ) : (
          <VideoPlayback
            asset={asset}
            muted={shouldMute}
            startPosition={startPosition}
            showFullScreenVideo={isFullScreenWatchLayout}
            withVideoInfoDetails={!isFullScreenWatchLayout}
            playback={actualPlayback}
            playbackError={error}
            onPlaybackFinished={onPlaybackFinished}
            onPlaybackPositionChanges={onPlaybackPositionChanges}
          />
        )}
        {!isNextVideoAutoPlayDisabled &&
          !isNextPreviewDisabled &&
          countdown && (
            <PlaylistCountdown
              countdown={countdown}
              onUserCancelCountdown={onUserCancelCountdown}
              nextAsset={nextAsset}
              onClick={onElementClick}
            />
          )}
      </div>
      {showPlaylistItems && (
        <PlaylistItems
          assets={assets}
          isHidden={isHidden}
          currentlyPlayingId={currentlyPlayingId}
          onClick={onElementClick}
        />
      )}
    </>
  );
};
